import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Alert, Box, Button, Link, Paper, Stack, TextField, Typography } from '@mui/material';
import { authApi } from '../../shared/api/auth';
import { AuthLayout } from '../components/AuthLayout';

const schema = z.object({
  email: z.string().email('Enter a valid email address'),
});

type FormValues = z.infer<typeof schema>;

export default function ForgotPasswordPage(): JSX.Element {
  const [done, setDone] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  const onSubmit = handleSubmit(async (values) => {
    setServerError(null);
    try {
      await authApi.forgotPassword(values.email);
      setDone(true);
    } catch {
      setServerError('Something went wrong. Please try again.');
    }
  });

  return (
    <AuthLayout title="Forgot password">
      <Paper sx={{ p: 4, width: '100%', maxWidth: 400 }} elevation={2}>
        <Typography variant="h5" gutterBottom>
          Reset your password
        </Typography>
        {done ? (
          <Stack spacing={2}>
            <Alert severity="success">
              If an account exists for that email, you'll receive a link to reset your password.
            </Alert>
            <Link component={RouterLink} to="/login">
              Back to sign in
            </Link>
          </Stack>
        ) : (
          <Box component="form" onSubmit={onSubmit} noValidate>
            <Stack spacing={2}>
              {serverError && <Alert severity="error">{serverError}</Alert>}
              <Typography variant="body2" color="text.secondary">
                Enter your account email and we'll send a reset link.
              </Typography>
              <TextField
                label="Email"
                type="email"
                autoComplete="email"
                error={Boolean(errors.email)}
                helperText={errors.email?.message}
                {...register('email')}
              />
              <Button type="submit" variant="contained" disabled={isSubmitting} size="large">
                {isSubmitting ? 'Sending…' : 'Send reset link'}
              </Button>
              <Link component={RouterLink} to="/login" variant="body2">
                Back to sign in
              </Link>
            </Stack>
          </Box>
        )}
      </Paper>
    </AuthLayout>
  );
}
